import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  MoreHorizontal,
  Check,
  X,
  CalendarRange,
  Clock,
  CheckCircle,
  XCircle,
  Building2,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { DataTable } from "@/components/admin/DataTable";
import {
  getBookings,
  getBuildings,
  type BookingFilters,
} from "@/lib/admin/queries";
import {
  approveBooking,
  denyBooking,
  cancelBooking,
} from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgBookingConflicts, useResolveBookingConflict } from "@/hooks/useBookingConflicts";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Booking, BookingStatus } from "@/types/database";

// ─── Types ────────────────────────────────────────────────────────────────────

type BookingRow = Booking & {
  unit?: { id: string; unit_number: string; organization_id: string } | null;
  host?: {
    id: string;
    nights_used: number;
    night_limit: number;
    status: string;
    profile?: { id: string; full_name: string; email: string } | null;
  } | null;
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdminBookingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [buildingId, setBuildingId] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Modal state
  const [denyTarget, setDenyTarget] = useState<BookingRow | null>(null);
  const [denyReason, setDenyReason] = useState("");
  const [cancelTarget, setCancelTarget] = useState<BookingRow | null>(null);

  // ─── Queries ──────────────────────────────────────────────────────────────

  const filters: BookingFilters = {
    ...(search ? { search } : {}),
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(buildingId !== "all" ? { buildingId } : {}),
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  };

  const { data: bookingsResult, isLoading } = useQuery({
    queryKey: ["admin", "bookings", filters, page, pageSize],
    queryFn: () => getBookings(filters, page, pageSize),
  });

  const { data: buildingsResult } = useQuery({
    queryKey: ["admin", "buildings-list"],
    queryFn: () => getBuildings({}, 1, 100),
  });

  const { data: bookingConflicts = [] } = useOrgBookingConflicts();
  const resolveConflict = useResolveBookingConflict();

  const bookings = (bookingsResult?.data ?? []) as BookingRow[];
  const totalCount = bookingsResult?.count ?? 0;
  const buildings = buildingsResult?.data ?? [];

  // Summary stats from current page
  const pending = bookings.filter((b) => b.status === "pending").length;
  const approved = bookings.filter((b) => b.status === "approved").length;
  const denied = bookings.filter((b) => b.status === "blocked").length;

  // ─── Mutations ────────────────────────────────────────────────────────────

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveBooking(id, user!.id),
    onSuccess: () => {
      toast.success("Booking approved");
      queryClient.invalidateQueries({ queryKey: ["admin", "bookings"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const denyMutation = useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      denyBooking(vars.id, vars.reason, user!.id),
    onSuccess: () => {
      toast.success("Booking denied");
      queryClient.invalidateQueries({ queryKey: ["admin", "bookings"] });
      setDenyTarget(null);
      setDenyReason("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelBooking(id, user!.id),
    onSuccess: () => {
      toast.success("Booking cancelled");
      queryClient.invalidateQueries({ queryKey: ["admin", "bookings"] });
      setCancelTarget(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ─── Export ───────────────────────────────────────────────────────────────

  function handleExport() {
    const rows = bookings.map((b) => ({
      id: b.id,
      building: (b as Record<string, unknown>).organization_name as string ?? "",
      unit: b.unit?.unit_number ?? "",
      host: b.host?.profile?.full_name ?? "",
      guest_name: b.guest_name,
      check_in: b.check_in,
      check_out: b.check_out,
      nights: b.nights,
      status: b.status,
      submitted: b.created_at,
    }));
    const header = Object.keys(rows[0] ?? {}).join(",");
    const csv = [header, ...rows.map((r) => Object.values(r).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bookings-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── Columns ──────────────────────────────────────────────────────────────

  const columns = [
    {
      key: "id",
      label: "ID",
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return (
          <span className="font-mono text-xs text-gray-500 whitespace-nowrap">
            {b.id.slice(0, 8)}
          </span>
        );
      },
    },
    {
      key: "building",
      label: "Building",
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return (
          <span className="text-sm font-medium text-gray-900 truncate max-w-[140px] block">
            {(b as Record<string, unknown> & { organization?: { name?: string } }).organization?.name ?? "—"}
          </span>
        );
      },
    },
    {
      key: "unit",
      label: "Unit",
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return (
          <span className="font-mono text-sm whitespace-nowrap">
            {b.unit?.unit_number ?? "—"}
          </span>
        );
      },
    },
    {
      key: "host",
      label: "Host",
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return (
          <span className="text-sm text-gray-700 truncate max-w-[140px] block">
            {b.host?.profile?.full_name ?? "—"}
          </span>
        );
      },
    },
    {
      key: "guest_name",
      label: "Guest Name",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return <span className="text-sm font-medium truncate max-w-[140px] block">{b.guest_name}</span>;
      },
    },
    {
      key: "check_in",
      label: "Check-in",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return (
          <span className="text-sm tabular-nums text-gray-600 whitespace-nowrap">
            {format(new Date(b.check_in), "MMM d, yyyy")}
          </span>
        );
      },
    },
    {
      key: "check_out",
      label: "Check-out",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return (
          <span className="text-sm tabular-nums text-gray-600 whitespace-nowrap">
            {format(new Date(b.check_out), "MMM d, yyyy")}
          </span>
        );
      },
    },
    {
      key: "nights",
      label: "Nights",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return <span className="tabular-nums text-sm whitespace-nowrap">{b.nights}</span>;
      },
    },
    {
      key: "status",
      label: "Status",
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        const statusMap: Record<BookingStatus, string> = {
          pending: "pending",
          approved: "approved",
          blocked: "rejected",
          completed: "completed",
          cancelled: "cancelled",
        };
        return (
          <div className="flex items-center gap-1.5">
            <StatusBadge status={statusMap[b.status] ?? b.status} size="sm" />
            {b.status === "pending" && (
              <div className="flex items-center gap-0.5 ml-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-green-600 hover:bg-green-50 hover:text-green-700"
                  title="Approve"
                  disabled={approveMutation.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    approveMutation.mutate(b.id);
                  }}
                  aria-label="Quick approve"
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-red-600 hover:bg-red-50 hover:text-red-700"
                  title="Deny"
                  disabled={denyMutation.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDenyTarget(b);
                    setDenyReason("");
                  }}
                  aria-label="Quick deny"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "created_at",
      label: "Submitted",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return (
          <span className="text-xs tabular-nums text-gray-400 whitespace-nowrap">
            {format(new Date(b.created_at), "MMM d, yyyy")}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row: Record<string, unknown>) => {
        const b = row as unknown as BookingRow;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => navigate(`/admin/bookings/${b.id}`)}
              >
                View detail
              </DropdownMenuItem>
              {b.status === "pending" && (
                <>
                  <DropdownMenuItem
                    className="text-green-700 whitespace-nowrap"
                    onClick={() => approveMutation.mutate(b.id)}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Approve
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-red-600 whitespace-nowrap"
                    onClick={() => { setDenyTarget(b); setDenyReason(""); }}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Deny…
                  </DropdownMenuItem>
                </>
              )}
              {b.status === "approved" && (
                <DropdownMenuItem
                  className="text-red-600 whitespace-nowrap"
                  onClick={() => setCancelTarget(b)}
                >
                  Cancel booking
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => {
                  if (b.host?.profile?.email) {
                    window.location.href = `mailto:${b.host.profile.email}`;
                  }
                }}
              >
                Contact host
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => {
                  if (b.guest_email) {
                    window.location.href = `mailto:${b.guest_email}`;
                  }
                }}
              >
                Contact guest
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // ─── Filters UI ───────────────────────────────────────────────────────────

  const filtersNode = (
    <>
      <Select
        value={statusFilter}
        onValueChange={(v) => { setStatusFilter(v); setPage(1); }}
      >
        <SelectTrigger className="h-9 w-[140px] text-sm">
          <SelectValue placeholder="All statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
          <SelectItem value="approved">Approved</SelectItem>
          <SelectItem value="blocked">Denied</SelectItem>
          <SelectItem value="cancelled">Cancelled</SelectItem>
          <SelectItem value="completed">Completed</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={buildingId}
        onValueChange={(v) => { setBuildingId(v); setPage(1); }}
      >
        <SelectTrigger className="h-9 w-[160px] text-sm">
          <Building2 className="mr-2 h-4 w-4 text-gray-400" />
          <SelectValue placeholder="All buildings" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All buildings</SelectItem>
          {buildings.map((bld) => (
            <SelectItem key={bld.id} value={bld.id}>
              {bld.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center gap-1.5">
        <CalendarRange className="h-4 w-4 text-gray-400 shrink-0" />
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className="h-9 w-[130px] text-sm"
          aria-label="Check-in from"
        />
        <span className="text-gray-400 text-sm">—</span>
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className="h-9 w-[130px] text-sm"
          aria-label="Check-in to"
        />
      </div>
    </>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
      </div>

      {/* Booking conflicts */}
      {bookingConflicts.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
            <AlertTriangle className="h-4 w-4" />
            {bookingConflicts.length} overlapping booking{bookingConflicts.length > 1 ? "s" : ""} detected
          </div>
          <div className="space-y-2">
            {bookingConflicts.map((c) => (
              <div key={`${c.booking_id}-${c.conflicting_booking_id}`} className="rounded-md border border-red-200 bg-white px-3 py-2 space-y-1.5">
                {[
                  { id: c.booking_id, guest: c.guest_name, checkIn: c.check_in, checkOut: c.check_out, platform: c.platform, synced: c.synced_from_ical },
                  { id: c.conflicting_booking_id, guest: c.conflicting_guest_name, checkIn: c.conflicting_check_in, checkOut: c.conflicting_check_out, platform: c.conflicting_platform, synced: c.conflicting_synced_from_ical },
                ].map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-3">
                    <span className="text-xs text-gray-700">
                      <span className="font-medium">{b.guest}</span>{" "}
                      {b.checkIn} → {b.checkOut} {b.synced && `· synced from ${b.platform ?? "calendar"}`}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[11px] shrink-0 border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => resolveConflict.mutate(b.id)}
                      disabled={resolveConflict.isPending}
                    >
                      Cancel this one
                    </Button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary stats bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 whitespace-nowrap">
          <span className="h-2 w-2 rounded-full bg-gray-400" />
          Total: <span className="tabular-nums">{totalCount}</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-700 whitespace-nowrap">
          <Clock className="h-3.5 w-3.5" />
          Pending: <span className="tabular-nums">{pending}</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1.5 text-sm font-medium text-green-700 whitespace-nowrap">
          <CheckCircle className="h-3.5 w-3.5" />
          Approved: <span className="tabular-nums">{approved}</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 whitespace-nowrap">
          <XCircle className="h-3.5 w-3.5" />
          Denied: <span className="tabular-nums">{denied}</span>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={bookings as unknown as Record<string, unknown>[]}
        loading={isLoading}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        onSearch={(q) => { setSearch(q); setPage(1); }}
        searchPlaceholder="Search booking ID, guest, or host…"
        selectedRows={selectedRows}
        onRowSelect={setSelectedRows}
        bulkActions={[{ label: "Export CSV", onClick: handleExport }]}
        onExport={handleExport}
        filters={filtersNode}
        emptyMessage="No bookings found. Adjust your filters."
      />

      {/* Deny Dialog */}
      <Dialog
        open={!!denyTarget}
        onOpenChange={(v) => { if (!v) { setDenyTarget(null); setDenyReason(""); } }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deny Booking</DialogTitle>
            <DialogDescription>
              Provide a reason for denying{" "}
              <strong>{denyTarget?.guest_name}</strong>'s booking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="deny-reason" className="whitespace-nowrap">Reason *</Label>
            <Textarea
              id="deny-reason"
              rows={3}
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              placeholder="e.g. Exceeds night cap, missing documentation…"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="whitespace-nowrap"
              onClick={() => { setDenyTarget(null); setDenyReason(""); }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="whitespace-nowrap"
              disabled={!denyReason.trim() || denyMutation.isPending}
              onClick={() =>
                denyTarget &&
                denyMutation.mutate({ id: denyTarget.id, reason: denyReason })
              }
            >
              {denyMutation.isPending ? "Denying…" : "Deny Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirm */}
      <ConfirmModal
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={() => cancelTarget && cancelMutation.mutate(cancelTarget.id)}
        title="Cancel Booking"
        description={`Cancel the booking for ${cancelTarget?.guest_name}? This action is logged.`}
        loading={cancelMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
